import ReactModal from "react-modal";

/**
 * Modal dialog that displays FTP instructions for a given URL.
 *
 * @param prop.ftpUrl FTP URL (may be ftps:// or ftp://)
 * @param prop.isFtpModalOpen Whether to display the modal
 * @param props.setFtpModalOpen Sets the value of isFtpModalOpen
 * @return JSX
 */
export function FtpInstructionsModal(props) {
    const {ftpUrl, isFtpModalOpen, setFtpModalOpen} = props;
    const ftpUrlObj = new URL(ftpUrl.replace('ftp', 'http'));

    const modalStyle = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.75)'
        },
        content: {
            position: 'absolute',
            top: '10%',
            left: '20%',
            right: '20%',
            bottom: '20%',
            border: '1px solid #ccc',
            background: '#fff',
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            borderRadius: '4px',
            outline: 'none',
            padding: '20px'
        }
    };

    const onRequestClose = () => {
        const newObj = {};
        newObj[ftpUrl] = false;
        setFtpModalOpen({...isFtpModalOpen, ...newObj});
    };

    return (
        <ReactModal
          style={modalStyle}
          isOpen={isFtpModalOpen}
          onRequestClose={onRequestClose}>
          <div className="ftp-instructions-modal-inner">
            <div className="ftp-instructions-modal-header">FTP Connection</div>

            <div className="ftp-instructions-modal-connection">
              <table>
                <tbody>
                  <tr>
                    <td className="ftp-instructions-modal-connection-left-col">URL</td>
                    <td className="ftp-instructions-modal-connection-right-col">
                      <div className="ftp-instructions-modal-connection-item">
                        <a href={ftpUrl}>
                          <code>{ftpUrl}</code>
                        </a>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="ftp-instructions-modal-connection-left-col">Hostname</td>
                    <td className="ftp-instructions-modal-connection-right-col">
                      <div className="ftp-instructions-modal-connection-item">
                        <a href={ftpUrl.split('//')[0] + "//" + ftpUrlObj.hostname}>
                          <code>{ftpUrlObj.hostname}</code>
                        </a>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="ftp-instructions-modal-connection-left-col">Path</td>
                    <td className="ftp-instructions-modal-connection-right-col">
                      <div className="ftp-instructions-modal-connection-item">
                        <code>{ftpUrlObj.pathname}</code>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

            </div>

            <div className="ftp-instructions-modal-header">Software Instructions</div>

            <p>
              FTP (File Transfer Protocol) is a network protocol used for transferring
              files between a client and a server on a computer network.
            </p>
            <p>
              To access and utilize FTP links efficiently, you'll need suitable software known
              as an FTP client. Such a clients act as interfaces between your device and the FTP server,
              facilitating file transfers.
            </p>
            <br />

            <div className="ftp-instructions-all">
              <div className="ftp-grid-row">
                <div className="ftp-grid-container">
                  <div className="ftp-instructions-instructions-grid-left-col">
                    <div className="ftp-instructions-modal-subheader">Windows</div>
                  </div>
                  <div className="ftp-instructions-instructions-grid-right-col">
                    Free and open-source graphical FTP clients for Windows include&nbsp;
                    <a href="https://filezilla-project.org/" target="_blank" rel="noreferrer">FileZilla</a>&nbsp;
                    <a href="https://wiki.filezilla-project.org/FileZilla_Client_Tutorial_(en)" target="_blank" rel="noreferrer">(tutorial)</a>,&nbsp;
                    <a href="https://winscp.net/" target="_blank" rel="noreferrer">WinSCP</a>&nbsp;
                    <a href="https://winscp.net/eng/docs/guides" target="_blank" rel="noreferrer">(tutorial)</a>,&nbsp;
                    and&nbsp;
                    <a href="https://cyberduck.io/" target="_blank" rel="noreferrer">Cyberduck</a>&nbsp;
                    <a href="https://docs.cyberduck.io/cyberduck/" target="_blank" rel="noreferrer">(tutorial)</a>.
                  </div>
                </div>
              </div>
              <div className="ftp-grid-row">
                <div className="ftp-grid-container">
                  <div className="ftp-instructions-instructions-grid-left-col">
                    <div className="ftp-instructions-modal-subheader">MacOS</div>
                  </div>
                  <div className="ftp-instructions-instructions-grid-right-col">
                    Free and open-source graphical FTP clients for MacOS include&nbsp;
                    <a href="https://filezilla-project.org/" target="_blank" rel="noreferrer">FileZilla</a>&nbsp;
                    <a href="https://wiki.filezilla-project.org/FileZilla_Client_Tutorial_(en)" target="_blank" rel="noreferrer">(tutorial)</a>,&nbsp;
                    and&nbsp;
                    <a href="https://cyberduck.io/" target="_blank" rel="noreferrer">Cyberduck</a>&nbsp;
                    <a href="https://docs.cyberduck.io/cyberduck/" target="_blank" rel="noreferrer">(tutorial)</a>.
                  </div>
                </div>
              </div>
              <div className="ftp-grid-row">
                <div className="ftp-grid-container">
                  <div className="ftp-instructions-instructions-grid-left-col">
                    <div className="ftp-instructions-modal-subheader">Linux</div>
                  </div>
                  <div className="ftp-instructions-instructions-grid-right-col"><div>
                    On Linux, the <code>lftp</code> command can be used.
                    It may need to be installed, but should be available through most package managers.

                    <pre className="ftp-instructions-code ftp-instructions-instructions-grid-responsive-pre">
                      {`lftp -u anonymous,lftp@ ${ftpUrlObj.hostname + ftpUrlObj.pathname}`}
                      ls
                    </pre>

                    To change directory, do:
                    <pre className="ftp-instructions-code">
                      cd &lt;dirname&gt;<br />
                    </pre>

                    To download all files, use the <code>mget</code> command:
                    <pre className="ftp-instructions-code">
                      mget *
                    </pre>

                    To download a single file, use the <code>get</code> command:
                    <pre className="ftp-instructions-code">
                      get &lt;filename&gt;<br />
                    </pre></div>
                  </div>
                </div>
              </div>
            </div>

            <button onClick={onRequestClose} className="ftp-instruction-blue-button">
              Close
            </button>
            <br />
          </div>
        </ReactModal>
    );
}
